import { useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowLeft, Box, CheckCircle2, ChevronRight, Loader2, Minus, PackagePlus, PackageX, Plus, ShoppingBag, X } from "lucide-react";
import { DestinatarioPortal, PedidoPortal, PedidoPortalDetalhe, ProdutoPortal, portalClienteApi } from "../lib/portalClienteApi";

type Tela = "acesso" | "codigo" | "destinatarios" | "comprar" | "pedidos";
type Canal = "whatsapp" | "email";
type LinhaCarrinho = ProdutoPortal & { quantidade: number };
type VisualizacaoProdutos = "cards-3" | "cards-4" | "tabela";

const moeda = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const dataBr = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" });

export default function PortalCliente() {
  const [tela, setTela] = useState<Tela>("acesso");
  const [canal, setCanal] = useState<Canal>("whatsapp");
  const [contato, setContato] = useState("");
  const [codigo, setCodigo] = useState("");
  const [destinatarios, setDestinatarios] = useState<DestinatarioPortal[]>([]);
  const [destinatario, setDestinatario] = useState<DestinatarioPortal | null>(null);
  const [produtos, setProdutos] = useState<ProdutoPortal[]>([]);
  const [pedidos, setPedidos] = useState<PedidoPortal[]>([]);
  const [pedidoDetalhe, setPedidoDetalhe] = useState<PedidoPortalDetalhe | null>(null);
  const [carrinho, setCarrinho] = useState<LinhaCarrinho[]>([]);
  const [busca, setBusca] = useState("");
  const [visualizacaoProdutos, setVisualizacaoProdutos] = useState<VisualizacaoProdutos>("cards-3");
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  useEffect(() => {
    portalClienteApi.sessao()
      .then(({ destinatarios }) => {
        setDestinatarios(destinatarios);
        setTela("destinatarios");
      })
      .catch(() => undefined);
  }, []);

  const produtosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return produtos;
    return produtos.filter((produto) => produto.cod.toLowerCase().includes(termo) || (produto.descricao || "").toLowerCase().includes(termo));
  }, [busca, produtos]);

  const totalCarrinho = useMemo(() => carrinho.reduce((total, item) => total + item.precoFinal * item.quantidade, 0), [carrinho]);

  function limparMensagens() {
    setErro("");
    setSucesso("");
  }

  async function solicitarCodigo(event: React.FormEvent) {
    event.preventDefault();
    limparMensagens();
    setCarregando(true);
    try {
      await portalClienteApi.solicitarCodigo(canal, contato);
      setTela("codigo");
      setSucesso("Código enviado.");
    } catch (error: any) {
      setErro(error.message || "Não foi possível enviar o código.");
    } finally {
      setCarregando(false);
    }
  }

  async function validarCodigo(event: React.FormEvent) {
    event.preventDefault();
    limparMensagens();
    setCarregando(true);
    try {
      const resultado = await portalClienteApi.validarCodigo(canal, contato, codigo);
      setDestinatarios(resultado.destinatarios);
      setTela("destinatarios");
    } catch (error: any) {
      setErro(error.message || "Não foi possível validar o código.");
    } finally {
      setCarregando(false);
    }
  }

  async function escolherDestinatario(destinatarioId: number) {
    limparMensagens();
    setCarregando(true);
    try {
      const resultado = await portalClienteApi.selecionarDestinatario(destinatarioId);
      setDestinatario(resultado.destinatario);
      const catalogo = await portalClienteApi.catalogo(resultado.destinatario.id);
      setProdutos(catalogo.produtos);
      setCarrinho([]);
      setTela("comprar");
    } catch (error: any) {
      setErro(error.message || "Não foi possível selecionar o destinatário.");
    } finally {
      setCarregando(false);
    }
  }

  function adicionarProduto(produto: ProdutoPortal) {
    if (produto.disponibilidade !== "disponivel") return;
    setCarrinho((atual) => {
      const existente = atual.find((item) => item.id === produto.id);
      if (existente) return atual.map((item) => item.id === produto.id ? { ...item, quantidade: item.quantidade + 1 } : item);
      return [...atual, { ...produto, quantidade: 1 }];
    });
  }

  function alterarQuantidade(productId: number, quantidade: number) {
    setCarrinho((atual) => quantidade <= 0 ? atual.filter((item) => item.id !== productId) : atual.map((item) => item.id === productId ? { ...item, quantidade } : item));
  }

  async function abrirPedidos() {
    if (!destinatario) return;
    limparMensagens();
    setCarregando(true);
    try {
      const resultado = await portalClienteApi.pedidos(destinatario.id);
      setPedidos(resultado.pedidos);
      setTela("pedidos");
    } catch (error: any) {
      setErro(error.message || "Não foi possível carregar os pedidos.");
    } finally {
      setCarregando(false);
    }
  }

  async function abrirDetalhePedido(pedidoId: number) {
    if (!destinatario) return;
    limparMensagens();
    setCarregando(true);
    try {
      const resultado = await portalClienteApi.detalhePedido(destinatario.id, pedidoId);
      setPedidoDetalhe(resultado.pedido);
    } catch (error: any) {
      setErro(error.message || "Não foi possível carregar os detalhes do pedido.");
    } finally {
      setCarregando(false);
    }
  }

  async function enviarCarrinho() {
    if (!destinatario || !carrinho.length) return;
    limparMensagens();
    setCarregando(true);
    try {
      const resultado = await portalClienteApi.enviarOrcamento(destinatario.id, carrinho.map((item) => ({ productId: item.id, quantidade: item.quantidade })));
      setCarrinho([]);
      setSucesso(`Orçamento #${resultado.numero} enviado.`);
    } catch (error: any) {
      setErro(error.message || "Não foi possível enviar o orçamento.");
    } finally {
      setCarregando(false);
    }
  }

  const mensagem = erro || sucesso;
  const classeMensagem = erro ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700";

  if (tela === "acesso" || tela === "codigo") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 p-5 text-slate-950">
        <section className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-orange-600 p-8 text-white">
            <div className="mb-8 flex h-11 w-11 items-center justify-center rounded-xl bg-white/15"><Box className="h-6 w-6" /></div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-200">Box</p>
            <h1 className="mt-2 text-3xl font-bold">Portal do Cliente</h1>
            <p className="mt-2 text-sm text-slate-200">Acesse sua mesa de compra com um código enviado ao comprador cadastrado.</p>
          </div>
          <div className="p-7">
            {mensagem && <div className={`mb-5 flex items-center gap-2 rounded-xl border p-3 text-sm ${classeMensagem}`}>{erro ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}{mensagem}</div>}
            {tela === "acesso" ? (
              <form className="space-y-5" onSubmit={solicitarCodigo}>
                <div className="grid grid-cols-2 rounded-xl bg-slate-100 p-1 text-sm font-semibold">
                  <button type="button" onClick={() => setCanal("whatsapp")} className={`rounded-lg px-3 py-2 ${canal === "whatsapp" ? "bg-white text-slate-950 shadow-sm" : "text-slate-500"}`}>WhatsApp</button>
                  <button type="button" onClick={() => setCanal("email")} className={`rounded-lg px-3 py-2 ${canal === "email" ? "bg-white text-slate-950 shadow-sm" : "text-slate-500"}`}>E-mail</button>
                </div>
                <label className="block text-sm font-semibold text-slate-700">{canal === "whatsapp" ? "WhatsApp do comprador" : "E-mail do comprador"}<input className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none ring-orange-500 focus:ring-2" value={contato} onChange={(event) => setContato(event.target.value)} type={canal === "email" ? "email" : "tel"} required /></label>
                <button disabled={carregando} className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 font-bold text-white transition hover:bg-orange-600 disabled:opacity-60">{carregando && <Loader2 className="h-4 w-4 animate-spin" />}Enviar código<ChevronRight className="h-4 w-4" /></button>
              </form>
            ) : (
              <form className="space-y-5" onSubmit={validarCodigo}>
                <div><p className="text-sm font-semibold text-slate-700">Digite o código recebido</p><p className="mt-1 text-sm text-slate-500">O código tem seis dígitos e permanece válido por 24 horas.</p></div>
                <input className="w-full rounded-xl border border-slate-300 px-4 py-4 text-center text-2xl font-bold tracking-[0.55em] outline-none ring-orange-500 focus:ring-2" inputMode="numeric" maxLength={6} value={codigo} onChange={(event) => setCodigo(event.target.value.replace(/\D/g, ""))} required />
                <button disabled={carregando} className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 font-bold text-white transition hover:bg-orange-600 disabled:opacity-60">{carregando && <Loader2 className="h-4 w-4 animate-spin" />}Validar código</button>
                <button type="button" className="flex w-full items-center justify-center gap-2 text-sm font-semibold text-slate-600" onClick={() => { limparMensagens(); setTela("acesso"); }}><ArrowLeft className="h-4 w-4" />Voltar</button>
              </form>
            )}
          </div>
        </section>
      </main>
    );
  }

  if (tela === "destinatarios") {
    return (
      <main className="min-h-screen bg-slate-50 p-5 text-slate-950 sm:p-8">
        <section className="mx-auto max-w-3xl">
          <div className="mb-8 flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500 text-white"><Box className="h-5 w-5" /></div><div><p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Box</p><h1 className="text-2xl font-bold">Escolha o destinatário</h1></div></div>
          {erro && <div className={`mb-5 flex items-center gap-2 rounded-xl border p-3 text-sm ${classeMensagem}`}><AlertCircle className="h-4 w-4" />{erro}</div>}
          <div className="grid gap-3">{destinatarios.map((item) => <button key={item.id} disabled={carregando} onClick={() => escolherDestinatario(item.id)} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-orange-300 hover:shadow disabled:opacity-60"><div><p className="font-bold text-slate-900">{item.nome}</p><p className="mt-1 text-sm text-slate-500">{item.emitenteNome}</p></div><ChevronRight className="h-5 w-5 text-orange-500" /></button>)}</div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-8"><div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500 text-white"><Box className="h-5 w-5" /></div><div><p className="text-xs font-semibold uppercase tracking-[0.14em] text-orange-600">{destinatario?.emitenteNome}</p><p className="text-sm font-bold">{destinatario?.nome}</p></div></div><nav className="flex items-center gap-2"><button onClick={() => setTela("comprar")} className={`rounded-lg px-3 py-2 text-sm font-semibold ${tela === "comprar" ? "bg-slate-100" : "text-slate-600"}`}>Comprar</button><button onClick={abrirPedidos} className={`rounded-lg px-3 py-2 text-sm font-semibold ${tela === "pedidos" ? "bg-slate-100" : "text-slate-600"}`}>Meus pedidos</button><button onClick={() => { setDestinatario(null); setCarrinho([]); setTela("destinatarios"); }} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600">Trocar</button></nav></div>
      </header>
      <section className="mx-auto max-w-7xl px-5 py-7 sm:px-8">
        {mensagem && <div className={`mb-5 flex items-center gap-2 rounded-xl border p-3 text-sm ${classeMensagem}`}>{erro ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}{mensagem}</div>}
        {tela === "pedidos" ? (
          <>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 p-5"><h1 className="text-xl font-bold">Meus pedidos</h1></div>
              {carregando ? <p className="p-8 text-slate-500">Carregando...</p> : pedidos.length ? (
                <div className="divide-y divide-slate-100">
                  {pedidos.map((pedido) => (
                    <button key={pedido.numero} type="button" onClick={() => abrirDetalhePedido(pedido.numero)} className="flex w-full items-center justify-between p-5 text-left transition hover:bg-slate-50">
                      <div><p className="font-bold">Pedido #{pedido.numero}</p><p className="text-sm text-slate-500">{dataBr.format(new Date(pedido.criadoEm))}</p></div>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">{pedido.status}</span>
                    </button>
                  ))}
                </div>
              ) : <p className="p-8 text-slate-500">Nenhum pedido encontrado.</p>}
            </div>
            {pedidoDetalhe && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-label={`Detalhes do pedido ${pedidoDetalhe.numero}`}>
                <section className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
                  <header className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
                    <div><p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-600">Pedido</p><h2 className="mt-1 text-2xl font-bold">#{pedidoDetalhe.numero}</h2><p className="mt-1 text-sm text-slate-500">{dataBr.format(new Date(pedidoDetalhe.criadoEm))}</p></div>
                    <button type="button" onClick={() => setPedidoDetalhe(null)} className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900" aria-label="Fechar detalhes"><X className="h-5 w-5" /></button>
                  </header>
                  <div className="space-y-5 p-5">
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 p-4"><span className="text-sm font-medium text-slate-600">Status</span><span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-slate-700 shadow-sm">{pedidoDetalhe.status}</span></div>
                    <div><h3 className="mb-3 font-bold">Itens</h3><div className="divide-y divide-slate-100 rounded-xl border border-slate-200">{pedidoDetalhe.itens.map((item, index) => <div key={`${item.cod}-${index}`} className="flex items-start justify-between gap-4 p-4"><div><p className="text-sm font-semibold">{item.cod}</p><p className="mt-1 text-sm text-slate-600">{item.descricao}</p><p className="mt-2 text-xs text-slate-500">{item.quantidade} × {moeda.format(item.valorUnitario)}</p></div><strong className="shrink-0 text-sm">{moeda.format(item.valorTotal)}</strong></div>)}</div></div>
                    <div className="flex items-center justify-between border-t border-slate-200 pt-5 text-lg font-bold"><span>Total do pedido</span><span>{moeda.format(pedidoDetalhe.valor)}</span></div>
                  </div>
                </section>
              </div>
            )}
          </>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
            <section>
              <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-bold">Mesa de compra</h1>
                  <p className="mt-1 text-sm text-slate-500">Preços aplicados automaticamente.</p>
                </div>
                <div className="flex w-full flex-wrap items-center justify-end gap-3 sm:w-auto">
                  <input className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 sm:w-72" placeholder="Buscar por código ou descrição" value={busca} onChange={(event) => setBusca(event.target.value)} />
                  <div className="flex rounded-xl border border-slate-300 bg-white p-1 text-xs font-semibold text-slate-600" aria-label="Visualização dos produtos">
                    <button type="button" onClick={() => setVisualizacaoProdutos("cards-3")} aria-pressed={visualizacaoProdutos === "cards-3"} className={`rounded-lg px-3 py-2 transition ${visualizacaoProdutos === "cards-3" ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>3 cards</button>
                    <button type="button" onClick={() => setVisualizacaoProdutos("cards-4")} aria-pressed={visualizacaoProdutos === "cards-4"} className={`rounded-lg px-3 py-2 transition ${visualizacaoProdutos === "cards-4" ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>4 cards</button>
                    <button type="button" onClick={() => setVisualizacaoProdutos("tabela")} aria-pressed={visualizacaoProdutos === "tabela"} className={`rounded-lg px-3 py-2 transition ${visualizacaoProdutos === "tabela" ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>Tabela</button>
                  </div>
                </div>
              </div>
              {carregando ? <p className="py-16 text-center text-slate-500">Carregando...</p> : visualizacaoProdutos === "tabela" ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr><th className="px-4 py-3 font-semibold">Código</th><th className="px-4 py-3 font-semibold">Produto</th><th className="px-4 py-3 font-semibold">Preço</th><th className="px-4 py-3 font-semibold">Disponibilidade</th><th className="px-4 py-3 text-right font-semibold">Ação</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {produtosFiltrados.map((produto) => (
                        <tr key={produto.id} className="hover:bg-slate-50">
                          <td className="px-4 py-4 font-semibold text-slate-600">{produto.cod}</td>
                          <td className="px-4 py-4 font-medium text-slate-900">{produto.descricao || "Sem descrição"}</td>
                          <td className="px-4 py-4 whitespace-nowrap"><span className="mr-2 text-xs text-slate-400 line-through">{moeda.format(produto.precoOriginal)}</span><strong className="text-orange-600">{moeda.format(produto.precoFinal)}</strong></td>
                          <td className={`px-4 py-4 whitespace-nowrap font-medium ${produto.disponibilidade === "disponivel" ? "text-emerald-700" : "text-slate-500"}`}>{produto.disponibilidade === "disponivel" ? "Disponível" : "Indisponível"}</td>
                          <td className="px-4 py-4 text-right"><button disabled={produto.disponibilidade !== "disponivel"} onClick={() => adicionarProduto(produto)} className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500">Adicionar</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={`grid gap-4 sm:grid-cols-2 ${visualizacaoProdutos === "cards-4" ? "xl:grid-cols-4" : "xl:grid-cols-3"}`}>
                  {produtosFiltrados.map((produto) => (
                    <article key={produto.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex aspect-[4/3] items-center justify-center bg-slate-100">{produto.fotoPortalUrl ? <img src={produto.fotoPortalUrl} alt={produto.descricao || produto.cod} className="h-full w-full object-cover" /> : null}</div>
                      <div className="space-y-3 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{produto.cod}</p>
                        <h2 className="min-h-10 text-sm font-semibold">{produto.descricao || "Sem descrição"}</h2>
                        <div><span className="mr-2 text-xs text-slate-400 line-through">{moeda.format(produto.precoOriginal)}</span><strong className="text-lg text-orange-600">{moeda.format(produto.precoFinal)}</strong></div>
                        <div className={`flex items-center gap-2 text-sm font-medium ${produto.disponibilidade === "disponivel" ? "text-emerald-700" : "text-slate-500"}`}>{produto.disponibilidade === "disponivel" ? <CheckCircle2 className="h-4 w-4" /> : <PackageX className="h-4 w-4" />}{produto.disponibilidade === "disponivel" ? "Disponível" : "Indisponível"}</div>
                        <button disabled={produto.disponibilidade !== "disponivel"} onClick={() => adicionarProduto(produto)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"><PackagePlus className="h-4 w-4" />Adicionar</button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
            <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-24">
              <div className="mb-4 flex items-center justify-between"><h2 className="font-bold">Carrinho</h2><ShoppingBag className="h-5 w-5 text-orange-500" /></div>
              {carrinho.length === 0 ? <p className="py-8 text-center text-sm text-slate-500">Nenhum item adicionado.</p> : <div className="space-y-4">{carrinho.map((item) => <div key={item.id} className="border-b border-slate-100 pb-4"><p className="text-sm font-semibold">{item.cod}</p><p className="line-clamp-2 text-xs text-slate-500">{item.descricao}</p><div className="mt-3 flex items-center justify-between"><div className="flex items-center gap-2"><button onClick={() => alterarQuantidade(item.id, item.quantidade - 1)} className="rounded-md border border-slate-300 p-1"><Minus className="h-3 w-3" /></button><span className="w-5 text-center text-sm font-bold">{item.quantidade}</span><button onClick={() => alterarQuantidade(item.id, item.quantidade + 1)} className="rounded-md border border-slate-300 p-1"><Plus className="h-3 w-3" /></button></div><strong className="text-sm">{moeda.format(item.precoFinal * item.quantidade)}</strong></div></div>)}<div className="flex items-center justify-between border-t border-slate-200 pt-4 text-lg font-bold"><span>Total</span><span>{moeda.format(totalCarrinho)}</span></div><button disabled={carregando || !carrinho.length} onClick={enviarCarrinho} className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 font-bold text-white disabled:opacity-60">{carregando && <Loader2 className="h-4 w-4 animate-spin" />}Enviar orçamento</button></div>}
            </aside>
          </div>
        )}
      </section>
    </main>
  );
}
